// Shared content: media, ecosystem verticals, audience paths, form schemas

export const MEDIA = {
  heroSkyline: "https://images.pexels.com/photos/29175968/pexels-photo-29175968.jpeg",
  waterfront: "https://images.pexels.com/photos/33397572/pexels-photo-33397572.jpeg",
  executive: "https://images.unsplash.com/photo-1718209881007-c0ecdfc00f9d",
  network: "https://images.pexels.com/photos/10325707/pexels-photo-10325707.png",
  building: "https://images.pexels.com/photos/7963572/pexels-photo-7963572.jpeg",
};

export const VERTICALS = [
  {
    key: "capital",
    label: "Moaglee Capital",
    desc: "Capital requests, private investors, creative finance, sale-leasebacks, and syndication deal flow.",
    path: "/capital",
    redirectKey: "capital",
    cta: "Explore Capital",
  },
  {
    key: "media",
    label: "Moaglee Media",
    desc: "AI-powered marketing, GHL automation, content systems, CRM follow-up, and lead conversion.",
    path: "/media",
    redirectKey: "media",
    cta: "Build My System",
  },
  {
    key: "cre",
    label: "Moaglee CRE",
    desc: "A private commercial real estate network for owners, sellers, tenants, investors, and off-market opportunities.",
    path: "/cre",
    redirectKey: "cre",
    cta: "View CRE",
  },
  {
    key: "michael",
    label: "Michael Brown",
    desc: "Commercial real estate broker & business strategist structuring better outcomes through real estate, capital & media.",
    path: "/michael-brown",
    redirectKey: null,
    cta: "Meet Michael",
  },
  {
    key: "aspire",
    label: "Aspire Perspire Inspire",
    desc: "The mindset, book, coaching, and discipline platform behind the Moaglee ecosystem.",
    path: "/aspire",
    redirectKey: "aspire",
    cta: "Join the List",
  },
];

export const AUDIENCE = [
  { label: "I Own Property", path: "/cre", form: "property_owner" },
  { label: "I Want to Sell", path: "/cre", form: "property_owner" },
  { label: "I Need Capital", path: "/capital", form: "capital_request" },
  { label: "I Want to Invest", path: "/capital", form: "investor" },
  { label: "I Need Commercial Space", path: "/cre", form: "tenant_requirement" },
  { label: "I Need Media / Automation", path: "/media", form: "media_audit" },
  { label: "I Want Private Deal Flow", path: "/private-network", form: "private_network" },
  { label: "I Want Coaching / Book Updates", path: "/aspire", form: "coaching" },
  { label: "I Want to Work With Michael", path: "/book-call", form: "strategy_call" },
];

// Form schemas — extra fields stored in `data`
const SELL_OUTCOMES = ["Sell", "Lease", "Refinance", "Raise Capital", "Reposition", "Private Discussion"];

export const FORMS = {
  property_owner: {
    title: "Property Owner Intake",
    lead_type: "property_owner",
    fields: [
      { name: "address", label: "Property Address", type: "text" },
      { name: "property_type", label: "Property Type", type: "text" },
      { name: "building_size", label: "Approx. Building Size (sq ft)", type: "text" },
      { name: "occupancy", label: "Current Occupancy (%)", type: "text" },
      { name: "desired_outcome", label: "Desired Outcome", type: "select", options: SELL_OUTCOMES },
      { name: "estimated_value", label: "Estimated Value", type: "text" },
    ],
  },
  investor: {
    title: "Investor Intake",
    lead_type: "investor",
    fields: [
      { name: "investment_range", label: "Investment Range", type: "text" },
      { name: "asset_type", label: "Preferred Asset Type", type: "text" },
      { name: "market", label: "Preferred Market", type: "text" },
      { name: "accredited", label: "Accredited Investor?", type: "select", options: ["Yes", "No"] },
      { name: "syndication", label: "Interested in Syndication?", type: "select", options: ["Yes", "No"] },
      { name: "private_listings", label: "Interested in Private Listings?", type: "select", options: ["Yes", "No"] },
    ],
  },
  capital_request: {
    title: "Capital Request",
    lead_type: "capital_request",
    fields: [
      { name: "entity_name", label: "Business / Entity Name", type: "text" },
      { name: "capital_amount", label: "Capital Amount Requested", type: "text" },
      { name: "use_of_funds", label: "Use of Funds", type: "text" },
      { name: "collateral", label: "Asset or Collateral", type: "text" },
      { name: "timeline", label: "Timeline", type: "text" },
      { name: "structure", label: "Desired Structure", type: "select", options: ["Loan", "Equity", "Sale-Leaseback", "Seller Carry", "Refinance", "Partnership"] },
    ],
  },
  tenant_requirement: {
    title: "Tenant Requirement",
    lead_type: "tenant_requirement",
    fields: [
      { name: "business_type", label: "Business Type", type: "text" },
      { name: "location", label: "Desired Location", type: "text" },
      { name: "square_footage", label: "Desired Square Footage", type: "text" },
      { name: "budget", label: "Budget", type: "text" },
      { name: "use_type", label: "Use Type", type: "text" },
      { name: "parking", label: "Parking Needs", type: "text" },
      { name: "timing", label: "Timing", type: "text" },
      { name: "special", label: "Special Requirements", type: "text" },
    ],
  },
  media_audit: {
    title: "Media System Audit",
    lead_type: "media_audit",
    fields: [
      { name: "business_name", label: "Business Name", type: "text" },
      { name: "website", label: "Website", type: "text" },
      { name: "current_crm", label: "Current CRM", type: "text" },
      { name: "channels", label: "Current Marketing Channels", type: "text" },
      { name: "lead_volume", label: "Monthly Lead Volume", type: "text" },
      { name: "biggest_problem", label: "Biggest Problem", type: "text" },
      { name: "desired_outcome", label: "Desired Outcome", type: "text" },
    ],
  },
  coaching: {
    title: "Coaching / Book Interest",
    lead_type: "coaching",
    fields: [
      { name: "interest_type", label: "Interest Type", type: "select", options: ["Book", "Coaching", "Speaking", "Workshop", "90-Day Program"] },
      { name: "main_goal", label: "Main Goal", type: "text" },
    ],
  },
  private_network: {
    title: "Private Network Application",
    lead_type: "private_network",
    fields: [
      { name: "role", label: "Your Role", type: "select", options: ["Owner", "Investor", "Tenant", "Broker", "Developer", "Business Owner", "Service Provider"] },
      { name: "looking_for", label: "What are you looking for?", type: "text" },
      { name: "control", label: "What do you control?", type: "text" },
      { name: "opportunity_type", label: "What opportunities do you want?", type: "text" },
    ],
  },
  strategy_call: {
    title: "Book a Strategy Call",
    lead_type: "strategy_call",
    fields: [
      { name: "topic", label: "What do you want to discuss?", type: "text" },
    ],
  },
};

export const ROLE_LABELS = {
  visitor: "Visitor",
  private_network_member: "Private Network Member",
  investor: "Investor",
  property_owner: "Property Owner",
  tenant_business_owner: "Tenant / Business Owner",
  media_client: "Media Client",
  coaching_member: "Coaching Member",
  admin: "Admin",
};
